/** Bookings: upcoming list, services + staff, hours. Route /sites/:id/bookings */
import React, { useEffect, useState } from 'react';
import {
    Button, Card, Checkbox, Empty, Input, InputNumber, Popconfirm, Select, Space, Switch, Table, Tabs, Tag, Typography, message,
} from 'antd';
import { DeleteOutlined, PlusOutlined, SaveOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import { toastErr, useLoad, useSite } from './common';

const { Text } = Typography;
const DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABEL = { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' };
const zones = typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : ['UTC'];
const parseHours = (s) => String(s || '').split(',').map((x) => x.trim()).filter(Boolean).map((x) => x.split('-').map((y) => y.trim()));
const showHours = (h) => (h || []).map((x) => x.join('-')).join(', ');

const Calendar = () => {
    const { t } = useTranslation();
    const { api } = useSite();
    const [past, setPast] = useState(false);
    const from = past ? new Date(Date.now() - 90 * 86400000).toISOString() : new Date().toISOString();
    const { data, reload, loading } = useLoad(() => api.get('/bookings', past ? { from, to: new Date().toISOString() } : { from }), [past]);
    return (
        <>
            <Checkbox checked={past} onChange={(e) => setPast(e.target.checked)} style={{ marginBottom: 8 }}>{t('Past 90 days')}</Checkbox>
            <Table size="small" rowKey="id" loading={loading} dataSource={data?.items || []} pagination={{ pageSize: 50, hideOnSinglePage: true }} scroll={{ x: 640 }}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No bookings yet')} /> }} columns={[
                    { title: t('Time'), dataIndex: 'local', width: 150 },
                    { title: t('Service'), dataIndex: 'service' },
                    { title: t('Staff'), dataIndex: 'staff' },
                    { title: t('Customer'), key: 'c', render: (_, b) => <span>{b.name}<br /><Text type="secondary">{b.email} {b.phone}</Text></span> },
                    { title: t('Notes'), dataIndex: 'note' },
                    { title: t('Status'), dataIndex: 'status', width: 100, render: (s) => <Tag color={s === 'confirmed' ? 'green' : 'default'}>{t(s === 'confirmed' ? 'Confirmed' : 'Cancelled')}</Tag> },
                    { title: '', key: 'x', width: 80, render: (_, b) => (b.status === 'confirmed' ? <Popconfirm title={t('Cancel booking')} onConfirm={() => api.post(`/bookings/${b.id}/cancel`).then(reload).catch(toastErr)}><Button size="small" danger>{t('Cancel')}</Button></Popconfirm> : null) },
                ]} />
        </>
    );
};

const Services = () => {
    const { t } = useTranslation();
    const { api } = useSite();
    const { data, reload } = useLoad(() => api.get('/booking/services'), [api]);
    const [rows, setRows] = useState([]);
    const [staff, setStaff] = useState([]);
    useEffect(() => { setRows(data?.services || []); setStaff(data?.staff || []); }, [data]);
    const set = (setter, i, p) => setter((xs) => xs.map((x, j) => (j === i ? { ...x, ...p } : x)));
    const saveS = (r) => (r.id ? api.put(`/booking/services/${r.id}`, r) : api.post('/booking/services', r)).then(() => { message.success(t('Saved')); reload(); }).catch(toastErr);
    const saveT = (r) => (r.id ? api.put(`/booking/staff/${r.id}`, r) : api.post('/booking/staff', r)).then(() => { message.success(t('Saved')); reload(); }).catch(toastErr);
    return (
        <Space direction="vertical" style={{ width: '100%' }} size="large">
            <div>
                <Text strong>{t('Services')}</Text>
                <Table size="small" rowKey={(r, i) => r.id || `n${i}`} pagination={false} dataSource={rows} scroll={{ x: 720 }} columns={[
                    { title: t('Name'), dataIndex: 'name', render: (v, _, i) => <Input size="small" value={v} onChange={(e) => set(setRows, i, { name: e.target.value })} /> },
                    { title: t('Minutes'), dataIndex: 'duration_min', width: 90, render: (v, _, i) => <InputNumber size="small" min={5} max={1440} value={v} onChange={(x) => set(setRows, i, { duration_min: x })} /> },
                    { title: t('Buffer'), dataIndex: 'buffer_min', width: 90, render: (v, _, i) => <InputNumber size="small" min={0} max={480} value={v} onChange={(x) => set(setRows, i, { buffer_min: x })} /> },
                    { title: t('Staff'), dataIndex: 'staff_ids', width: 200, render: (v, _, i) => <Select size="small" mode="multiple" style={{ width: '100%' }} value={v || []} onChange={(x) => set(setRows, i, { staff_ids: x })} options={staff.filter((s) => s.id).map((s) => ({ value: s.id, label: s.name }))} /> },
                    { title: '', key: 'a', width: 80, render: (_, r) => (
                        <Space size={0}>
                            <Button size="small" type="text" icon={<SaveOutlined />} onClick={() => saveS(r)} />
                            {r.id && <Popconfirm title={t('Delete?')} onConfirm={() => api.del(`/booking/services/${r.id}`).then(reload).catch(toastErr)}><Button size="small" type="text" danger icon={<DeleteOutlined />} /></Popconfirm>}
                        </Space>
                    ) },
                ]} />
                <Button size="small" type="dashed" icon={<PlusOutlined />} style={{ marginTop: 6 }} onClick={() => setRows([...rows, { name: '', duration_min: 30, buffer_min: 0, staff_ids: [] }])}>{t('Add service')}</Button>
            </div>
            <div>
                <Text strong>{t('Staff')}</Text>
                <Table size="small" rowKey={(r, i) => r.id || `n${i}`} pagination={false} dataSource={staff} scroll={{ x: 480 }} columns={[
                    { title: t('Name'), dataIndex: 'name', render: (v, _, i) => <Input size="small" value={v} onChange={(e) => set(setStaff, i, { name: e.target.value })} /> },
                    { title: t('Email'), dataIndex: 'email', render: (v, _, i) => <Input size="small" value={v || ''} onChange={(e) => set(setStaff, i, { email: e.target.value })} /> },
                    { title: '', key: 'a', width: 80, render: (_, r) => (
                        <Space size={0}>
                            <Button size="small" type="text" icon={<SaveOutlined />} onClick={() => saveT(r)} />
                            {r.id && <Popconfirm title={t('Delete?')} onConfirm={() => api.del(`/booking/staff/${r.id}`).then(reload).catch(toastErr)}><Button size="small" type="text" danger icon={<DeleteOutlined />} /></Popconfirm>}
                        </Space>
                    ) },
                ]} />
                <Button size="small" type="dashed" icon={<PlusOutlined />} style={{ marginTop: 6 }} onClick={() => setStaff([...staff, { name: '', email: '' }])}>{t('Add staff')}</Button>
            </div>
        </Space>
    );
};

const Hours = () => {
    const { t } = useTranslation();
    const { api } = useSite();
    const { data } = useLoad(() => api.get('/booking/settings'), [api]);
    const [v, setV] = useState(null);
    const [text, setText] = useState({});
    const [ex, setEx] = useState('');
    useEffect(() => {
        if (!data) return;
        setV(data);
        setText(Object.fromEntries(DAYS.map((d) => [d, showHours((data.weekly || {})[d])])));
        setEx((data.exceptions || []).map((e) => `${e.date} ${showHours(e.hours)}`.trim()).join('\n'));
    }, [data]);
    if (!v) return null;
    const save = async () => {
        try {
            await api.put('/booking/settings', {
                ...v,
                weekly: Object.fromEntries(DAYS.map((d) => [d, parseHours(text[d])])),
                exceptions: ex.split('\n').map((l) => l.trim()).filter(Boolean).map((l) => { const [date, ...rest] = l.split(/\s+/); return { date, hours: parseHours(rest.join(' ')) }; }),
            });
            message.success(t('Saved'));
        } catch (e) { toastErr(e); }
    };
    return (
        <Space direction="vertical" style={{ width: '100%', maxWidth: 520 }}>
            <Space><Switch checked={v.enabled} onChange={(enabled) => setV({ ...v, enabled })} /><Text>{t('Accept bookings')}</Text></Space>
            <Select showSearch style={{ width: '100%' }} value={v.timezone} onChange={(timezone) => setV({ ...v, timezone })} options={['UTC', ...zones.filter((z) => z !== 'UTC')].map((z) => ({ value: z, label: z }))} />
            {DAYS.map((d) => <Input key={d} addonBefore={<span style={{ display: 'inline-block', width: 36 }}>{t(DAY_LABEL[d])}</span>} placeholder={t('Closed')} value={text[d]} onChange={(e) => setText({ ...text, [d]: e.target.value })} />)}
            <Text type="secondary" style={{ fontSize: 12 }}>09:00-12:00, 13:00-17:00</Text>
            <Text strong>{t('Exceptions')}</Text>
            <Input.TextArea rows={3} placeholder="2026-12-25&#10;2026-12-31 09:00-12:00" value={ex} onChange={(e) => setEx(e.target.value)} />
            <Space wrap>
                <span>{t('Minimum notice (hours)')} <InputNumber min={0} max={720} value={v.notice_hours} onChange={(x) => setV({ ...v, notice_hours: x || 0 })} /></span>
                <span>{t('Book ahead (days)')} <InputNumber min={1} max={365} value={v.horizon_days} onChange={(x) => setV({ ...v, horizon_days: x || 1 })} /></span>
            </Space>
            <Input addonBefore={t('Send notifications to')} placeholder="you@example.com" value={v.notify_email || ''} onChange={(e) => setV({ ...v, notify_email: e.target.value })} />
            <Button type="primary" icon={<SaveOutlined />} onClick={save}>{t('Save')}</Button>
        </Space>
    );
};

const Bookings = () => {
    const { t } = useTranslation();
    return (
        <Card size="small">
            <Tabs items={[
                { key: 'cal', label: t('Bookings'), children: <Calendar /> },
                { key: 'svc', label: t('Services'), children: <Services /> },
                { key: 'hours', label: t('Hours'), children: <Hours /> },
            ]} />
        </Card>
    );
};

export default Bookings;
